import moment from "moment";

/**
 * getMetricWeeklyArray
 * Transforms an array of daily chart records into a 7-element array (Mon–Sun)
 * where each element is [min, max, media] for the given metric key.
 *
 * @param {Array}  dataArray  - Array of daily records from chartData / prevChartData.
 * @param {string} metricKey  - Key to read from each record (e.g. "StressAvgAVG").
 * @param {number} domainMin  - Floor of the chart domain.
 * @param {number} domainMax  - Ceiling of the chart domain.
 * @returns {Array<[number, number, number]>} 7 elements, one per ISO weekday.
 */
export const getMetricWeeklyArray = (dataArray, metricKey, domainMin, domainMax) => {
  const result = Array(7)
    .fill(null)
    .map(() => [domainMin, domainMin, domainMin]);

  if (!dataArray || dataArray.length === 0) return result;

  dataArray.forEach((d) => {
    const dayIndex = moment(d.date).isoWeekday() - 1;
    if (dayIndex >= 0 && dayIndex < 7) {
      const val = d[metricKey];
      if (val !== undefined && val !== null) {
        const media = Number(val);
        let min, max;

        if (metricKey === "StressAvgAVG") {
          const spread = 12 + media * 0.1;
          min = Math.max(domainMin, media - spread);
          max = Math.min(domainMax, media + spread);
        } else if (metricKey === "StepsAVG") {
          const spread = media * 0.22;
          min = Math.max(domainMin, media - spread);
          max = Math.min(domainMax, media + spread);
        } else if (metricKey === "SleepHoursAVG") {
          const spread = 1.1;
          min = Math.max(domainMin, media - spread);
          max = Math.min(domainMax, media + spread);
        } else {
          const spread = media * 0.15;
          min = Math.max(domainMin, media - spread);
          max = Math.min(domainMax, media + spread);
        }

        result[dayIndex] = [min, max, media];
      }
    }
  });

  return result;
};

/**
 * calculateDynamicDomain
 * Derives chart domain bounds from the actual data in s1 and s2,
 * adding padding and clamping to sensible limits.
 *
 * @param {Array}  s1          - Previous-week weekly array.
 * @param {Array}  s2          - Current-week weekly array.
 * @param {number} defaultMin  - Fallback minimum.
 * @param {number} defaultMax  - Fallback maximum.
 * @returns {{ domainMin: number, domainMax: number }}
 */
export const calculateDynamicDomain = (s1, s2, defaultMin, defaultMax) => {
  const activeDaysValues = [...s1, ...s2]
    .filter(
      (day) =>
        day &&
        (day[0] !== defaultMin || day[1] !== defaultMin || day[2] !== defaultMin),
    )
    .flatMap((day) => [day[0], day[1], day[2]]);

  if (activeDaysValues.length === 0) {
    return { domainMin: defaultMin, domainMax: defaultMax };
  }

  let minVal = Math.min(...activeDaysValues);
  let maxVal = Math.max(...activeDaysValues);

  if (minVal === maxVal) {
    minVal = minVal * 0.9;
    maxVal = maxVal * 1.1;
  }

  const diff    = maxVal - minVal;
  const padding = diff * 0.15 || 1;

  const domainMin = Math.max(0, Math.floor(minVal - padding));
  const domainMax = Math.min(
    defaultMax * 1.2 || 100000,
    Math.ceil(maxVal + padding),
  );

  return { domainMin, domainMax };
};
